import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Spinner,
  Alert,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import { updateAgendaItem, getAssemblyById } from "endpoints/voting/index.action";
import "./CreatePollModal.scss";

interface EditPollModalProps {
  isOpen: boolean;
  toggle: () => void;
  poll: {
    id: string;
    title: string;
    description: string;
    votingStart?: string;
    votingEnd?: string;
    options?: { text: string; votes?: number }[];
    assemblyId?: string;
  } | null;
  onPollUpdated?: () => void;
}

interface PollOption {
  id: string;
  text: string;
}

const EditPollModal: React.FC<EditPollModalProps> = ({ isOpen, toggle, poll, onPollUpdated }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    startingDate: "",
    startingTime: "",
    endingDate: "",
    endingTime: "",
  });
  const [options, setOptions] = useState<PollOption[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasVotes, setHasVotes] = useState(false);
  const [assemblyDetails, setAssemblyDetails] = useState<{ startTime?: string; endTime?: string } | null>(null);

  // Memoize minDate to ensure it's stable and set to yesterday to allow selecting today
  const todayMinDate = useMemo(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);
    return yesterday;
  }, []);

  // Helper function to format date as YYYY-MM-DD using local timezone (not UTC)
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Load poll data when modal opens or poll changes
  useEffect(() => {
    if (isOpen && poll) {
      // Parse voting start date/time
      const startDate = poll.votingStart ? new Date(poll.votingStart) : new Date();
      const startDateStr = formatDateLocal(startDate);
      const startTimeStr = startDate.toTimeString().slice(0, 5) || ""; // HH:mm format

      // Parse voting end date/time
      const endDate = poll.votingEnd ? new Date(poll.votingEnd) : new Date();
      const endDateStr = formatDateLocal(endDate);
      const endTimeStr = endDate.toTimeString().slice(0, 5) || ""; // HH:mm format

      // Load options
      const pollOptions: PollOption[] = poll.options && poll.options.length > 0
        ? poll.options.map((opt, idx) => ({
            id: String(idx + 1),
            text: opt.text,
          }))
        : [
            { id: "1", text: "" },
            { id: "2", text: "" },
          ];

      // Check if poll has votes (cannot change options if votes exist)
      const hasExistingVotes = poll.options?.some(opt => (opt.votes || 0) > 0) || false;
      setHasVotes(hasExistingVotes);

      setFormData({
        title: poll.title || "",
        description: poll.description || "",
        startingDate: startDateStr,
        startingTime: startTimeStr,
        endingDate: endDateStr,
        endingTime: endTimeStr,
      });
      setOptions(pollOptions);
      setError(null);

      // Fetch assembly details if assemblyId exists
      if (poll.assemblyId) {
        getAssemblyById(poll.assemblyId)
          .then((response) => {
            if (response?.data) {
              setAssemblyDetails({
                startTime: response.data.startTime,
                endTime: response.data.endTime,
              });
            }
          })
          .catch((err) => {
            console.error("Error fetching assembly details:", err);
            // Don't block the form if assembly fetch fails
            setAssemblyDetails(null);
          });
      } else {
        setAssemblyDetails(null);
      }
    }
  }, [isOpen, poll]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOptionChange = (id: string, value: string) => {
    setOptions((prev) =>
      prev.map((opt) => (opt.id === id ? { ...opt, text: value } : opt))
    );
  };

  const addOption = () => {
    if (options.length >= 10) {
      setError("Maximum 10 poll options allowed");
      return;
    }
    const newId = String(Date.now());
    setOptions((prev) => [...prev, { id: newId, text: "" }]);
  };

  const removeOption = (id: string) => {
    if (options.length > 2) {
      setOptions((prev) => prev.filter((opt) => opt.id !== id));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate form
    if (!formData.title || !formData.description) {
      setError("Please fill in all required fields");
      return;
    }

    if (!formData.startingDate || !formData.startingTime || !formData.endingDate || !formData.endingTime) {
      setError("Please select all date and time fields");
      return;
    }

    // Validate options
    const validOptions = options.filter((opt) => opt.text.trim());
    if (validOptions.length < 2) {
      setError("Please add at least 2 poll options");
      return;
    }

    // Edge case: Cannot change options if votes have been cast
    if (hasVotes) {
      const currentOptions = poll?.options?.map(opt => opt.text) || [];
      const newOptions = validOptions.map(opt => opt.text.trim());
      
      // Check if options have changed
      const optionsChanged = 
        currentOptions.length !== newOptions.length ||
        currentOptions.some((opt, idx) => opt !== newOptions[idx]);

      if (optionsChanged) {
        setError("Cannot change poll options after votes have been cast");
        return;
      }
    }

    try {
      setSubmitting(true);

      // Combine date and time into ISO strings
      const startDateTime = new Date(`${formData.startingDate}T${formData.startingTime}`);
      const endDateTime = new Date(`${formData.endingDate}T${formData.endingTime}`);

      if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
        setError("Invalid date or time format");
        return;
      }

      // Edge case: Cannot set start time in the past
      const now = new Date();
      if (startDateTime < now) {
        setError("Cannot set voting start time in the past");
        return;
      }

      if (startDateTime >= endDateTime) {
        setError("End date/time must be after start date/time");
        return;
      }

      // Validate voting times against assembly times if assembly exists
      // Backend requires: votingStart >= assembly.startTime and votingEnd <= assembly.endTime
      if (assemblyDetails?.startTime && assemblyDetails?.endTime) {
        const assemblyStartTime = new Date(assemblyDetails.startTime);
        const assemblyEndTime = new Date(assemblyDetails.endTime);

        if (startDateTime < assemblyStartTime) {
          setError(`Voting start time must be on or after assembly start time (${assemblyStartTime.toLocaleString()})`);
          return;
        }

        if (endDateTime > assemblyEndTime) {
          setError(`Voting end time must be on or before assembly end time (${assemblyEndTime.toLocaleString()})`);
          return;
        }
      }

      // Prepare update data
      const updateData: {
        title?: string;
        description?: string;
        options?: string[];
        votingStart?: string;
        votingEnd?: string;
      } = {
        title: formData.title.trim(),
        description: formData.description?.trim() || undefined,
        votingStart: startDateTime.toISOString(),
        votingEnd: endDateTime.toISOString(),
      };

      // Only include options if they've changed and no votes exist
      if (!hasVotes) {
        updateData.options = validOptions.map(opt => opt.text.trim()).filter(opt => opt.length > 0);
        
        if (updateData.options.length < 2) {
          setError("Please add at least 2 poll options");
          return;
        }
      }

      // Update agenda item
      const updateResponse = await updateAgendaItem(poll!.id, updateData);

      if (!updateResponse?.data || updateResponse?.status !== 'success') {
        throw new Error(updateResponse?.message || "Failed to update poll");
      }

      // Success - close modal
      toggle();

      // Notify parent component to refresh polls list
      if (onPollUpdated) {
        onPollUpdated();
      } else {
        // Fallback: reload the page
        window.location.reload();
      }
    } catch (err: any) {
      console.error("Error updating poll:", err);
      setError(err?.message || "Failed to update poll. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    toggle();
  };

  if (!poll) return null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      size="lg"
      className="create-poll-modal"
    >
      <ModalHeader toggle={handleClose} className="create-poll-modal-header">
        Edit Poll
      </ModalHeader>
      <ModalBody className="create-poll-modal-body">
        <p className="create-poll-subtitle">
          Update poll details. Note: Options cannot be changed if votes have been cast.
        </p>
        {error && (
          <Alert color="danger" className="mb-3">
            {error}
          </Alert>
        )}
        {hasVotes && (
          <Alert color="warning" className="mb-3">
            <i className="ri-alert-line me-2"></i>
            <strong>Note:</strong> This poll has received votes. You cannot change the voting options, but you can update other details.
          </Alert>
        )}
        <Form onSubmit={handleSubmit} className="create-poll-form">
          <FormGroup className="create-poll-form-group">
            <Label for="title" className="create-poll-label">
              Title <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter poll title"
              className="create-poll-input"
              required
            />
          </FormGroup>

          <FormGroup className="create-poll-form-group">
            <Label for="description" className="create-poll-label">
              Description <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter poll description"
              rows={5}
              className="create-poll-textarea"
              required
            />
          </FormGroup>

          <Row className="create-poll-row">
            <Col md={6}>
              <FormGroup className="create-poll-form-group">
                <Label for="startingDate" className="create-poll-label">
                  Starting Date <span className="text-danger">*</span>
                </Label>
                <div className="create-poll-date-wrapper">
                  <Flatpickr
                    key={`start-date-${isOpen}`}
                    className="create-poll-input"
                    placeholder="Select Date"
                    value={formData.startingDate ? new Date(formData.startingDate + 'T00:00:00') : undefined}
                    onChange={(dates) => {
                      const date = dates[0];
                      if (date) {
                        const dateStr = formatDateLocal(date);
                        setFormData((prev) => ({
                          ...prev,
                          startingDate: dateStr,
                        }));
                      } else {
                        setFormData((prev) => ({
                          ...prev,
                          startingDate: "",
                        }));
                      }
                    }}
                    options={{
                      dateFormat: "Y-m-d",
                      minDate: todayMinDate,
                      allowInput: false,
                      clickOpens: true,
                      static: false,
                      wrap: false,
                    }}
                  />
                  <i className="ri-calendar-line create-poll-icon"></i>
                </div>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="create-poll-form-group">
                <Label for="startingTime" className="create-poll-label">
                  Starting Time <span className="text-danger">*</span>
                </Label>
                <div className="create-poll-date-wrapper">
                  <Flatpickr
                    className="create-poll-input"
                    placeholder="Select Time"
                    value={formData.startingTime ? (() => {
                      const timeParts = formData.startingTime.split(":");
                      const hours = timeParts[0] || "0";
                      const minutes = timeParts[1] || "0";
                      const date = new Date();
                      date.setHours(parseInt(hours, 10) || 0, parseInt(minutes, 10) || 0, 0, 0);
                      return date;
                    })() : undefined}
                    onChange={(dates) => {
                      const date = dates[0];
                      if (date) {
                        const hours = String(date.getHours()).padStart(2, "0");
                        const minutes = String(date.getMinutes()).padStart(2, "0");
                        setFormData((prev) => ({
                          ...prev,
                          startingTime: `${hours}:${minutes}`,
                        }));
                      } else {
                        setFormData((prev) => ({
                          ...prev,
                          startingTime: "",
                        }));
                      }
                    }}
                    options={{
                      enableTime: true,
                      noCalendar: true,
                      dateFormat: "H:i",
                      time_24hr: true,
                      allowInput: false,
                      clickOpens: true,
                    }}
                  />
                  <i className="ri-time-line create-poll-icon"></i>
                </div>
              </FormGroup>
            </Col>
          </Row>

          <Row className="create-poll-row">
            <Col md={6}>
              <FormGroup className="create-poll-form-group">
                <Label for="endingDate" className="create-poll-label">
                  Ending Date <span className="text-danger">*</span>
                </Label>
                <div className="create-poll-date-wrapper">
                  <Flatpickr
                    className="create-poll-input"
                    placeholder="Select Date"
                    value={formData.endingDate ? new Date(formData.endingDate + 'T00:00:00') : undefined}
                    onChange={(dates) => {
                      const date = dates[0];
                      if (date) {
                        const dateStr = formatDateLocal(date);
                        setFormData((prev) => ({
                          ...prev,
                          endingDate: dateStr,
                        }));
                      } else {
                        setFormData((prev) => ({
                          ...prev,
                          endingDate: "",
                        }));
                      }
                    }}
                    options={{
                      dateFormat: "Y-m-d",
                      minDate: formData.startingDate ? new Date(formData.startingDate + 'T00:00:00') : todayMinDate,
                      allowInput: false,
                      clickOpens: true,
                    }}
                  />
                  <i className="ri-calendar-line create-poll-icon"></i>
                </div>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="create-poll-form-group">
                <Label for="endingTime" className="create-poll-label">
                  Ending Time <span className="text-danger">*</span>
                </Label>
                <div className="create-poll-date-wrapper">
                  <Flatpickr
                    className="create-poll-input"
                    placeholder="Select Time"
                    value={formData.endingTime ? (() => {
                      const timeParts = formData.endingTime.split(":");
                      const hours = timeParts[0] || "0";
                      const minutes = timeParts[1] || "0";
                      const date = new Date();
                      date.setHours(parseInt(hours, 10) || 0, parseInt(minutes, 10) || 0, 0, 0);
                      return date;
                    })() : undefined}
                    onChange={(dates) => {
                      const date = dates[0];
                      if (date) {
                        const hours = String(date.getHours()).padStart(2, "0");
                        const minutes = String(date.getMinutes()).padStart(2, "0");
                        setFormData((prev) => ({
                          ...prev,
                          endingTime: `${hours}:${minutes}`,
                        }));
                      } else {
                        setFormData((prev) => ({
                          ...prev,
                          endingTime: "",
                        }));
                      }
                    }}
                    options={{
                      enableTime: true,
                      noCalendar: true,
                      dateFormat: "H:i",
                      time_24hr: true,
                      allowInput: false,
                      clickOpens: true,
                    }}
                  />
                  <i className="ri-time-line create-poll-icon"></i>
                </div>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup className="create-poll-form-group">
            <Label className="create-poll-label create-poll-options-label">
              Options <span className="text-danger">*</span>
              {hasVotes && (
                <span className="text-warning ms-2">
                  <i className="ri-lock-line me-1"></i>
                  Locked (votes exist)
                </span>
              )}
            </Label>
            <div className="create-poll-options-list">
              {options.map((option, idx) => (
                <div key={option.id} className="create-poll-option-item">
                  <Input
                    type="text"
                    placeholder={`Option ${idx + 1}`}
                    value={option.text}
                    onChange={(e) => handleOptionChange(option.id, e.target.value)}
                    className="create-poll-input create-poll-option-input"
                    required={idx < 2}
                    disabled={hasVotes}
                  />
                  {options.length > 2 && !hasVotes && (
                    <Button
                      type="button"
                      color="danger"
                      size="sm"
                      outline
                      onClick={() => removeOption(option.id)}
                      className="create-poll-remove-btn"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </Button>
                  )}
                </div>
              ))}
            </div>
            {!hasVotes && (
              <Button
                type="button"
                color="light"
                size="sm"
                onClick={addOption}
                className="create-poll-add-option-btn"
                disabled={options.length >= 10}
              >
                <i className="ri-add-line me-2"></i>
                Add Option {options.length >= 10 && "(Max 10)"}
              </Button>
            )}
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter className="create-poll-modal-footer">
        <Button 
          color="secondary" 
          onClick={handleClose} 
          className="create-poll-cancel-btn"
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button 
          color="primary" 
          onClick={handleSubmit} 
          className="create-poll-submit-btn"
          disabled={submitting}
        >
          {submitting ? (
            <>
              <Spinner size="sm" className="me-2" />
              Updating...
            </>
          ) : (
            <>
              Update Poll
              <i className="ri-save-line ms-2"></i>
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EditPollModal;

